import { invokeFunction } from '../api'

// ── Types ────────────────────────────────────────────────────────────────────

export interface StateRegulatoryBody {
  state_code: string
  agency_name: string
  agency_abbreviation: string | null
  jurisdiction_scope: string
  website_url: string | null
  licensing_authority_citation: string | null
  is_active: boolean
}

export interface StateCfcCrosswalk {
  state_code: string
  agency_name: string
  internal_control_id: string
  control_title: string
  control_domain: string
  cfr_section: string
  cfr_subsection: string | null
  cfr_name: string
  relationship_type: string
  gap_analysis_summary: string | null
  state_citation: string | null
  cms_cfc_citation: string | null
  resolution_note: string | null
}

export interface StateComparisonRow {
  cfr_section: string
  cfr_subsection: string | null
  cfr_name: string
  cfr_domain: string
  state_coverage: Record<string, string>
}

export interface CrosswalkCompleteness {
  state_code: string
  total_state_controls: number
  mapped_controls: number
  unmapped_controls: number
  coverage_pct: number
  total_cfc_sections: number
  covered_cfc_sections: number
  uncovered_cfc_sections: number
  cfc_coverage_pct: number
  unmapped_control_ids: string[]
  uncovered_cfc_section_ids: string[]
}

export interface StateStageSummary {
  stage: string
  state_code: string
  agency_name: string
  deterministic_signature: string
  counts: { controls: number; crosswalk_mappings: number }
  validation_errors: Array<{ field: string; error: string }>
}

export interface StateImportResult {
  stage: string
  state_code: string
  agency_name?: string
  deterministic_signature?: string
  counts?: { controls_upserted: number; crosswalk_mappings_upserted: number }
  validation_errors?: Array<{ field: string; error: string }>
}

// ── Error helper ─────────────────────────────────────────────────────────────

class ApiRequestError extends Error {
  constructor(message: string, public code?: string) {
    super(message)
    this.name = 'ApiRequestError'
  }
}

function toApiRequestError(error: { message: string; code?: string } | null, fallback: string): ApiRequestError {
  return new ApiRequestError(error?.message ?? fallback, error?.code)
}

// ── Service functions ────────────────────────────────────────────────────────

export async function getStateRegulatoryBodies(): Promise<StateRegulatoryBody[]> {
  const { data, error } = await invokeFunction<StateRegulatoryBody[]>('state-regulatory-import', {
    method: 'GET',
    path: '/bodies',
  })
  if (error) throw toApiRequestError(error, 'Failed to load state regulatory bodies')
  return data ?? []
}

export async function getStateGapAnalysis(
  stateCode: string,
  domain?: string,
  relationshipType?: string
): Promise<StateCfcCrosswalk[]> {
  const params = new URLSearchParams({ state_code: stateCode })
  if (domain) params.set('domain', domain)
  if (relationshipType) params.set('relationship_type', relationshipType)
  const { data, error } = await invokeFunction<StateCfcCrosswalk[]>('state-regulatory-import', {
    method: 'GET',
    path: `/gap-analysis?${params.toString()}`,
  })
  if (error) throw toApiRequestError(error, 'Failed to load gap analysis')
  return data ?? []
}

export async function getStateCoverageComparison(stateCodes: string[]): Promise<StateComparisonRow[]> {
  const params = new URLSearchParams()
  stateCodes.forEach((code) => params.append('states', code))
  const { data, error } = await invokeFunction<StateComparisonRow[]>('state-regulatory-import', {
    method: 'GET',
    path: `/compare?${params.toString()}`,
  })
  if (error) throw toApiRequestError(error, 'Failed to load coverage comparison')
  return data ?? []
}

export async function getCrosswalkCompleteness(stateCode: string): Promise<CrosswalkCompleteness | null> {
  const { data, error } = await invokeFunction<CrosswalkCompleteness>('state-regulatory-import', {
    method: 'GET',
    path: `/completeness?state_code=${stateCode}`,
  })
  if (error) throw toApiRequestError(error, 'Failed to load crosswalk completeness')
  return data ?? null
}

export async function stageStateImport(payload: unknown): Promise<StateStageSummary> {
  const { data, error } = await invokeFunction<StateStageSummary>('state-regulatory-import', {
    method: 'POST',
    path: '/stage',
    body: payload,
  })
  if (error) throw toApiRequestError(error, 'Staging failed')
  if (!data) throw new Error('No staging result returned')
  return data
}

export async function importStateRegulatory(payload: unknown): Promise<StateImportResult> {
  const { data, error } = await invokeFunction<StateImportResult>('state-regulatory-import', {
    method: 'POST',
    path: '/import',
    body: payload,
  })
  if (error) throw toApiRequestError(error, 'Import failed')
  if (!data) throw new Error('No import result returned')
  return data
}

// ── Permission helper ────────────────────────────────────────────────────────

export function canManageStateRegulatory(viewerContext: { is_superadmin: boolean } | null): boolean {
  return viewerContext?.is_superadmin === true
}

// ── Relationship type display helpers ────────────────────────────────────────

const RELATIONSHIP_COLORS: Record<string, string> = {
  equivalent: 'bg-green-100 text-green-800',
  stricter: 'bg-yellow-100 text-yellow-800',
  weaker: 'bg-orange-100 text-orange-800',
  gap: 'bg-red-100 text-red-800',
  defers_to_cms: 'bg-blue-100 text-blue-800',
  not_applicable: 'bg-gray-100 text-gray-500',
  no_data: 'bg-gray-50 text-gray-400',
}

const RELATIONSHIP_LABELS: Record<string, string> = {
  equivalent: 'Equivalent',
  stricter: 'Stricter',
  weaker: 'Weaker',
  gap: 'Gap',
  defers_to_cms: 'Defers to CMS',
  not_applicable: 'N/A',
  no_data: 'No Data',
}

export function getRelationshipColor(type: string): string {
  return RELATIONSHIP_COLORS[type] ?? RELATIONSHIP_COLORS.no_data
}

export function getRelationshipLabel(type: string): string {
  return RELATIONSHIP_LABELS[type] ?? type
}

// ── CSV export helper ────────────────────────────────────────────────────────

export function exportComparisonToCsv(rows: StateComparisonRow[], stateCodes: string[]): void {
  const header = ['CfC Section', 'Subsection', 'Name', 'Domain', ...stateCodes]
  const csvRows = rows.map((row) => [
    row.cfr_section,
    row.cfr_subsection ?? '',
    `"${(row.cfr_name ?? '').replace(/"/g, '""')}"`,
    row.cfr_domain ?? '',
    ...stateCodes.map((code) => row.state_coverage[code] ?? 'no_data'),
  ])

  const csv = [header.join(','), ...csvRows.map((r) => r.join(','))].join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `state-cfc-comparison-${stateCodes.join('-')}.csv`
  link.click()
  URL.revokeObjectURL(url)
}
